"use client";

import type { AssistantStaticBubbleVariantProps, UserStaticBubbleVariantProps } from "@ui/components/core/BaseChat";
import { MessagesList } from "@ui/components/core/BaseChat";
import { clsx } from "clsx";
import { AnimatePresence, LayoutGroup } from "motion/react";
import * as m from "motion/react-m";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { JSX } from "react";
import { getMessageAlignmentClassName } from "./alignment";
import { ChatScenarioAssistantTypingIndicator, ChatScenarioUserTypingIndicator } from "./ChatScenarioTypingIndicator";
import {
  AssistantScenarioMessage,
  SameBubbleAssistantScenarioMessage,
  SameBubbleUserScenarioMessage,
  StaticMessage,
  UserScenarioMessage,
} from "./messageRenderers";
import type { ChatScenarioData, ChatScenarioRole } from "./types";

const defaultAssistantMessageVariants: AssistantStaticBubbleVariantProps = { variant: "cream" };
const defaultUserMessageVariants: UserStaticBubbleVariantProps = { variant: "elevated" };

/** Spring used to animate existing rows smoothly when a new message shifts the stack. */
const rowLayoutTransition = { type: "spring", stiffness: 400, damping: 35, mass: 0.8 } as const;

interface ChatScenarioProps {
  scenario: ChatScenarioData;
  active?: boolean;
  className?: string;
  anchor?: "top" | "bottom" | "center";
  onComplete?: () => void;
  /** Fired when the number of mounted scripted messages changes during playback. */
  onVisibleMessageCountChange?: (visibleCount: number) => void;
  /**
   * Fired when the number of fully revealed messages changes (content shown, not typing/loading).
   * In `same-bubble` mode this lags behind `onVisibleMessageCountChange` while the next bubble types.
   */
  onSettledMessageCountChange?: (settledCount: number) => void;
  additionalMessageDelayMS?: number;
  additionalPartDelayMS?: number;
  /**
   * When true, shows an ellipsis bubble (styled as user vs assistant) during inter-message delays immediately before
   * the next message appears. Also shows assistant dots between parts of a multi-part assistant message when
   * `additionalPartDelayMS` is positive.
   */
  typingIndicatorBetweenMessages?: boolean;
  /**
   * - `separate-bubble` (default): typing indicator is rendered as its own bubble that unmounts when the next message
   *   reveals.
   * - `same-bubble`: each message owns its loading phase — the bubble shell appears first with typing dots inside, then
   *   morphs (opacity + blur) to its content. Layout animates the bubble's size change. Each message can override the
   *   duration via `message.loadingBeforeMs`; otherwise falls back to `additionalMessageDelayMS`.
   */
  typingMode?: "separate-bubble" | "same-bubble";
  /**
   * Show this many messages from the start of `scenario.messages` instantly in their final state when playback begins.
   * The animated reveal starts from the message at this index. Defaults to 0 (animate from the first message).
   */
  initialVisibleMessageCount?: number;
  /**
   * When true, applies a top-fade mask so messages fade out as they scroll off the top of the
   * visible area. Uses CSS `mask-image` so it works on any background color.
   */
  topFade?: boolean;
  assistantMessageVariants?: AssistantStaticBubbleVariantProps;
  userMessageVariants?: UserStaticBubbleVariantProps;
  /**
   * When set, splits the rendered message rows into two cells with a fixed-height gap between. The last
   * `bottomCount` rows (newest) render in the bottom cell; everything older renders in the top cell.
   * Cross-cell shared-element animation is enabled via `layoutId` on each row so a row "graduating" from
   * the bottom cell to the top cell animates across the gap. Default `gapPx` is 24.
   */
  splitConfig?: { bottomCount: number; gapPx?: number };
}

export function ChatScenario({
  scenario,
  active = false,
  className,
  anchor = "top",
  onComplete,
  onVisibleMessageCountChange,
  onSettledMessageCountChange,
  additionalMessageDelayMS = 350,
  additionalPartDelayMS = 300,
  typingIndicatorBetweenMessages = false,
  typingMode = "separate-bubble",
  initialVisibleMessageCount = 0,
  topFade = false,
  assistantMessageVariants = defaultAssistantMessageVariants,
  userMessageVariants = defaultUserMessageVariants,
  splitConfig,
}: ChatScenarioProps): JSX.Element {
  const isSameBubbleMode = typingMode === "same-bubble";
  const animatedFromIndex = Math.max(0, Math.min(Math.floor(initialVisibleMessageCount), scenario.messages.length));
  const instanceId = useId();
  const [visibleMessageCount, setVisibleMessageCount] = useState(0);
  const [settledMessageCount, setSettledMessageCount] = useState(0);
  const [playbackKey, setPlaybackKey] = useState(0);
  const [typingPresenceKey, setTypingPresenceKey] = useState(0);
  const [typingIndicatorRole, setTypingIndicatorRole] = useState<ChatScenarioRole | null>(null);
  /**
   * Tracks which role was typing before the newest message revealed, so `morphFromTyping` can be
   * computed correctly even though both `setVisibleMessageCount` and `setTypingIndicatorRole(null)`
   * are batched in the same render (making `typingIndicatorRole` already null at render time).
   */
  const morphRoleRef = useRef<ChatScenarioRole | null>(null);
  const onCompleteRef = useRef(onComplete);
  const onVisibleMessageCountChangeRef = useRef(onVisibleMessageCountChange);
  const onSettledMessageCountChangeRef = useRef(onSettledMessageCountChange);
  const playbackRunIdRef = useRef(0);
  const interMessageTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    onVisibleMessageCountChangeRef.current = onVisibleMessageCountChange;
  }, [onVisibleMessageCountChange]);

  useEffect(() => {
    onSettledMessageCountChangeRef.current = onSettledMessageCountChange;
  }, [onSettledMessageCountChange]);

  useEffect(() => {
    onVisibleMessageCountChangeRef.current?.(visibleMessageCount);
  }, [visibleMessageCount]);

  useEffect(() => {
    onSettledMessageCountChangeRef.current?.(settledMessageCount);
  }, [settledMessageCount]);

  const clearInterMessageTimeout = useCallback(() => {
    if (interMessageTimeoutRef.current !== null) {
      clearTimeout(interMessageTimeoutRef.current);
      interMessageTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      clearInterMessageTimeout();
    };
  }, [clearInterMessageTimeout]);

  const handleMessageComplete = useCallback(
    (messageIndex: number, playbackRunId: number) => {
      if (playbackRunId !== playbackRunIdRef.current) {
        return;
      }

      setSettledMessageCount(messageIndex + 1);

      const isLastMessage = messageIndex === scenario.messages.length - 1;

      if (isLastMessage) {
        clearInterMessageTimeout();
        setTypingIndicatorRole(null);
        morphRoleRef.current = null;
        onCompleteRef.current?.();
        return;
      }

      const currentMessage = scenario.messages[messageIndex];
      const nextMessage = scenario.messages[messageIndex + 1];
      const nextVisibleMessageCount = messageIndex + 2;
      const preTransitionWait =
        Math.max(0, currentMessage?.visibleDurationMs ?? 0) + Math.max(0, nextMessage?.delayBeforeMs ?? 0);

      clearInterMessageTimeout();

      const startTransition = () => {
        // Same-bubble mode: the next message owns its loading phase, so just mount it.
        if (isSameBubbleMode) {
          setVisibleMessageCount(nextVisibleMessageCount);
          return;
        }

        const nextMessageHasSpeechPart = nextMessage?.parts.some(p => p.type === "speech") ?? false;
        const nextRole =
          typingIndicatorBetweenMessages && nextMessage && !nextMessageHasSpeechPart ? nextMessage.role : null;

        if (nextRole) {
          setTypingPresenceKey(k => k + 1);
        }
        // Store role in ref NOW so the reveal render can detect it even after state is cleared.
        morphRoleRef.current = nextRole;
        setTypingIndicatorRole(nextRole);

        interMessageTimeoutRef.current = setTimeout(() => {
          interMessageTimeoutRef.current = null;
          // Batch both updates in one render: new message mounts and typing unmounts simultaneously,
          // producing a single layout shift instead of two back-to-back jumps.
          setVisibleMessageCount(nextVisibleMessageCount);
          setTypingIndicatorRole(null);
        }, additionalMessageDelayMS);
      };

      if (preTransitionWait > 0) {
        interMessageTimeoutRef.current = setTimeout(() => {
          interMessageTimeoutRef.current = null;
          startTransition();
        }, preTransitionWait);
      } else {
        startTransition();
      }
    },
    [
      additionalMessageDelayMS,
      clearInterMessageTimeout,
      isSameBubbleMode,
      scenario.messages,
      typingIndicatorBetweenMessages,
    ],
  );

  useEffect(() => {
    clearInterMessageTimeout();
    setTypingIndicatorRole(null);
    setTypingPresenceKey(0);
    morphRoleRef.current = null;

    const nextPlaybackRunId = playbackRunIdRef.current + 1;

    playbackRunIdRef.current = nextPlaybackRunId;
    setPlaybackKey(nextPlaybackRunId);

    if (!active) {
      setVisibleMessageCount(0);
      setSettledMessageCount(0);
      return;
    }

    if (scenario.messages.length === 0) {
      setVisibleMessageCount(0);
      setSettledMessageCount(0);
      onCompleteRef.current?.();
      return;
    }

    // Every message before `animatedFromIndex` renders as a StaticMessage (no animation, no loading).
    // If the static prefix already covers all messages, jump straight to "done".
    if (animatedFromIndex >= scenario.messages.length) {
      setVisibleMessageCount(scenario.messages.length);
      setSettledMessageCount(scenario.messages.length);
      onCompleteRef.current?.();
      return;
    }

    setSettledMessageCount(animatedFromIndex);

    const firstAnimatedMessage = scenario.messages[animatedFromIndex];
    const firstDelayMs = Math.max(0, firstAnimatedMessage?.delayBeforeMs ?? 0);
    if (firstDelayMs > 0) {
      // Statics are visible right away; schedule the first animated message after its delayBeforeMs.
      setVisibleMessageCount(animatedFromIndex);
      interMessageTimeoutRef.current = setTimeout(() => {
        interMessageTimeoutRef.current = null;
        setVisibleMessageCount(animatedFromIndex + 1);
      }, firstDelayMs);
      return;
    }

    setVisibleMessageCount(animatedFromIndex + 1);
  }, [active, animatedFromIndex, clearInterMessageTimeout, scenario.messages]);

  const staticVisibleCount = Math.min(visibleMessageCount, animatedFromIndex);
  const staticMessages = scenario.messages.slice(0, staticVisibleCount);
  const animatedVisibleMessages = scenario.messages.slice(animatedFromIndex, visibleMessageCount);

  const typingIndicatorVisible = !isSameBubbleMode && typingIndicatorBetweenMessages && typingIndicatorRole !== null;
  const totalRenderedRows =
    (scenario.initialMessages?.length ?? 0) +
    staticMessages.length +
    animatedVisibleMessages.length +
    (typingIndicatorVisible ? 1 : 0);
  const splitProp = splitConfig
    ? {
        atIndex: Math.max(0, totalRenderedRows - splitConfig.bottomCount),
        gapPx: splitConfig.gapPx ?? 24,
      }
    : undefined;

  return (
    <LayoutGroup id={instanceId}>
      <MessagesList
        anchor={anchor}
        className={clsx("tw-overflow-hidden", className)}
        contentClassName='tw-overflow-hidden tw-p-1.5 tw-[overflow-anchor:none]'
        split={splitProp}
        style={
          topFade
            ? {
                maskImage: "linear-gradient(to bottom, transparent 0%, black 30%)",
                WebkitMaskImage: "linear-gradient(to bottom, transparent 0%, black 30%)",
              }
            : undefined
        }
      >
        {scenario.initialMessages?.map((msg, idx) => {
          const key = msg.id ?? `initial-${idx}`;
          return (
            <m.div
              key={key}
              layout='position'
              layoutId={splitConfig ? `${instanceId}-initial-${key}` : undefined}
              transition={{ layout: rowLayoutTransition }}
            >
              <StaticMessage
                message={msg}
                assistantMessageVariants={assistantMessageVariants}
                userMessageVariants={userMessageVariants}
              />
            </m.div>
          );
        })}

        {staticMessages.map((msg, idx) => {
          const key = `static-prefix-${msg.id ?? idx}`;
          return (
            <m.div
              key={key}
              layout='position'
              layoutId={splitConfig ? `${instanceId}-${key}` : undefined}
              className={getMessageAlignmentClassName(msg.role)}
              transition={{ layout: rowLayoutTransition }}
            >
              <StaticMessage
                message={msg}
                assistantMessageVariants={assistantMessageVariants}
                userMessageVariants={userMessageVariants}
              />
            </m.div>
          );
        })}

        {animatedVisibleMessages.map((msg, localIndex) => {
          const index = animatedFromIndex + localIndex;
          if (isSameBubbleMode) {
            const firstPart = msg.parts[0];
            const firstPartIsText = firstPart?.type === "text";
            const loadingDurationMs = firstPartIsText
              ? typeof msg.loadingBeforeMs === "number" && Number.isFinite(msg.loadingBeforeMs)
                ? Math.max(0, msg.loadingBeforeMs)
                : Math.max(0, additionalMessageDelayMS)
              : 0;

            return (
              <m.div
                key={`${playbackKey}-${msg.id ?? index}`}
                layout='position'
                layoutId={splitConfig ? `${instanceId}-same-${playbackKey}-${msg.id ?? index}` : undefined}
                className={getMessageAlignmentClassName(msg.role)}
                transition={{ layout: rowLayoutTransition }}
              >
                {msg.role === "user" ? (
                  <SameBubbleUserScenarioMessage
                    assistantMessageVariants={assistantMessageVariants}
                    message={msg}
                    messageIndex={index}
                    playbackRunId={playbackKey}
                    onMessageComplete={handleMessageComplete}
                    loadingDurationMs={loadingDurationMs}
                    userMessageVariants={userMessageVariants}
                  />
                ) : (
                  <SameBubbleAssistantScenarioMessage
                    assistantMessageVariants={assistantMessageVariants}
                    message={msg}
                    messageIndex={index}
                    playbackRunId={playbackKey}
                    onMessageComplete={handleMessageComplete}
                    loadingDurationMs={loadingDurationMs}
                    partDelayMS={additionalPartDelayMS}
                    typingIndicatorBetweenAssistantParts={typingIndicatorBetweenMessages}
                    userMessageVariants={userMessageVariants}
                  />
                )}
              </m.div>
            );
          }

          // Read from ref: typingIndicatorRole is already null in this same render
          // (both state updates were batched together in the timer callback).
          const morphFromTyping =
            typingIndicatorBetweenMessages && index === visibleMessageCount - 1 && msg.role === morphRoleRef.current;

          return (
            <m.div
              key={`${playbackKey}-${msg.id ?? index}`}
              layout='position'
              layoutId={splitConfig ? `${instanceId}-anim-${playbackKey}-${msg.id ?? index}` : undefined}
              className={getMessageAlignmentClassName(msg.role)}
              transition={{ layout: rowLayoutTransition }}
            >
              {msg.role === "user" ? (
                <UserScenarioMessage
                  assistantMessageVariants={assistantMessageVariants}
                  message={msg}
                  messageIndex={index}
                  morphFromTyping={morphFromTyping}
                  playbackRunId={playbackKey}
                  onMessageComplete={handleMessageComplete}
                  userMessageVariants={userMessageVariants}
                />
              ) : (
                <AssistantScenarioMessage
                  assistantMessageVariants={assistantMessageVariants}
                  message={msg}
                  messageIndex={index}
                  morphFromTyping={morphFromTyping}
                  playbackRunId={playbackKey}
                  onMessageComplete={handleMessageComplete}
                  partDelayMS={additionalPartDelayMS}
                  typingIndicatorBetweenAssistantParts={typingIndicatorBetweenMessages}
                  userMessageVariants={userMessageVariants}
                />
              )}
            </m.div>
          );
        })}

        <AnimatePresence initial={false} mode='popLayout'>
          {!isSameBubbleMode && typingIndicatorBetweenMessages && typingIndicatorRole ? (
            <m.div
              key={`${playbackKey}-typing-${typingPresenceKey}-${typingIndicatorRole}`}
              layout='position'
              layoutId={
                splitConfig
                  ? `${instanceId}-typing-${playbackKey}-${typingPresenceKey}-${typingIndicatorRole}`
                  : undefined
              }
              className={getMessageAlignmentClassName(typingIndicatorRole)}
              exit={{
                opacity: 0,
                scale: 0.94,
                transition: { duration: 0.18, ease: [0.25, 0.1, 0.25, 1] },
              }}
              transition={{ layout: rowLayoutTransition }}
            >
              {typingIndicatorRole === "assistant" ? (
                <ChatScenarioAssistantTypingIndicator assistantMessageVariants={assistantMessageVariants} />
              ) : (
                <ChatScenarioUserTypingIndicator userMessageVariants={userMessageVariants} />
              )}
            </m.div>
          ) : null}
        </AnimatePresence>
      </MessagesList>
    </LayoutGroup>
  );
}
